import { Component, input, output, signal, effect } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NavigationService } from '../../../../services/navigation.service';
import { HtmlDescriptionFieldComponent } from '../../../fields/standard/html-description-field/html-description-field.component';
import { NameFieldComponent } from '../../../fields/standard/name-field/name-field.component';
import { TextFieldComponent } from '../../../fields/standard/text-field/text-field.component';
import { Content, ContentText, ContentType, Course, Rubric, TextUtils, _ } from '@mira-avant/mtlibs';
import { AppModule } from '../../../../modules/app.module';
import { ZorroModule } from '../../../../modules/zorro.module';
import { ThirdPartyModule } from '../../../../modules/third-party.module';
import { AngularModule } from '../../../../modules/angular.module';
import { TextareaFieldComponent } from '../../../fields/standard/textarea-field/textarea-field.component';
import { CheckboxFieldComponent } from '../../../fields/standard/checkbox-field/checkbox-field.component';
import { DropdownFieldComponent } from '../../../fields/standard/dropdown-field/dropdown-field.component';
import { DropdownOption } from '../../../controls/standard/dropdown-control/dropdown-control.component';
import { FormTitleComponent } from '../../../ui/forms/form-title/form-title.component';
import { PreviousImageSelectorComponent } from '../../../ui/admin/previous-image-selector/previous-image-selector.component';
import { ScoringMethodFieldComponent } from '../../../fields/standard/scoring-method-field/scoring-method-field.component';
import { RubricLevelDropdownFieldComponent } from '../../../fields/standard/rubric-level-dropdown-field/rubric-level-dropdown-field.component';
import { CourseService } from '../../../../services/course.service';
import { RubricService } from '../../../../services/rubric.service';
import { SkillCodeDropdownFieldComponent } from '../../../fields/standard/skill-code-dropdown-field/skill-code-dropdown-field.component';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';

@Component({
	selector: 'app-content-form',
	standalone: true,
	imports: [
		AppModule,
		ZorroModule,
		ThirdPartyModule,
		AngularModule,
		NameFieldComponent,
		HtmlDescriptionFieldComponent,
		TextFieldComponent,
		TextareaFieldComponent,
		CheckboxFieldComponent,
		DropdownFieldComponent,
		FormTitleComponent,
		PreviousImageSelectorComponent,
		ScoringMethodFieldComponent,
		RubricLevelDropdownFieldComponent,
		SkillCodeDropdownFieldComponent,
		NzTooltipModule,
	],
	templateUrl: './content-form.component.html',
	styleUrl: './content-form.component.scss',
})
export class ContentFormComponent {
	// Inputs as signals
	readonly content = input<Content | undefined>();
	readonly editLanguage = input<string | undefined>();

	// Output as signal-based
	readonly onSubmit = output<any>();

	// Reactive state
	readonly showPreviousImageSelector = signal<boolean>(false);
	readonly rubric = signal<Rubric | undefined>(undefined);

	public form: FormGroup = this.fb.group({
		contentType: ['', TextFieldComponent.validators],
		name: ['', NameFieldComponent.validators],
		descriptionHtml: ['', []],
		descriptionShort: ['', []],
		scoringMethod: ['', [Validators.required]],
		rubricLevelUuid: [null, []],
		skillCode: [null, []],
		isFeatured: [false, []],
		imageUri: ['', []],
		contentUri: ['', []],
		dataJson: ['', []],
	});

	readonly options: DropdownOption[] = [
		{ value: ContentType.H5P, label: 'H5P' },
		{ value: ContentType.DISPLAY, label: 'Wysiwyg' },
		{ value: ContentType.COACH, label: 'Hubs' },
	];

	constructor(
		private fb: FormBuilder,
		public nav: NavigationService,
		private courseService: CourseService,
		private rubricService: RubricService
	) {
		// Watch matrix params → load course → load rubric
		this.nav.matrixParams$.subscribe(async (matrixParams: { [key: string]: string }) => {
			const courseUuid = matrixParams['courseUuid'];

			if (!courseUuid) return;

			const course = await this.courseService.get$(courseUuid);

			if (!course?.rubric_code) {
				console.warn('Course or rubric code not found for UUID:', courseUuid);
				return;
			}

			const rubric = await this.rubricService.getRubric$(course.rubric_code);
			this.rubric.set(rubric);
		});

		// React to content + editLanguage changes
		effect(() => {
			const currentContent = this.content();
			const currentLang = this.editLanguage();

			if (!currentContent || !currentLang) {
				this.form.reset();
				return;
			}

			const contentText = currentContent.content_text.find(
				(text: ContentText) => text.default_lang_id === currentLang
			);

			this.form.patchValue({
				contentType: currentContent.content_type || '',
				name: contentText?.name || '',
				descriptionHtml: contentText?.description_html ?? null,
				descriptionShort: contentText?.description_short ?? null,
				scoringMethod: currentContent.scoring_method || '',
				rubricLevelUuid: currentContent.rubric_level_uuid || null,
				skillCode: currentContent.skill_code || null,
				isFeatured: currentContent.featured_mode || false,
				imageUri: contentText?.image_uri || '',
				contentUri: contentText?.content_uri || '',
				dataJson: contentText?.data_json ? JSON.stringify(contentText.data_json) : '',
			});
		});
	}

	public submit(): void {
		const value = this.form.getRawValue();

		this.onSubmit.emit({
			...value,
			descriptionShort: TextUtils.toOptionalText(value.descriptionShort),
			descriptionHtml: TextUtils.toOptionalText(value.descriptionHtml),
		});
	}

	public clear(): void {
		this.form.reset();
	}

	public selectImageUri(imageUri: string): void {
		this.form.patchValue({ imageUri });
		this.showPreviousImageSelector.set(false);
	}
}
